import { CancelTokenSource } from 'axios'
import { FileManipulationKey, VisualFileType, downloadVisualTypeToBaseType } from 'constants/visual'
import { Nullable, NullableStringIndexSignature } from 'models/helpers'
import { create } from 'zustand'

import { APIRequest } from 'models/API'
import { UrlDTO } from 'models/visuals'

export interface PurchaseSessionUploadImage {
  file: File
  type: VisualFileType
  deleting?: boolean
  signedUrl?: string
  originalFilename?: string
  progress: number
  cancelToken?: CancelTokenSource
  request: APIRequest<null>
  replaces?: string
  droppedIn: number
}

export interface PurchaseSessionDownloadImage {
  file?: File
  type: VisualFileType
  deleting?: boolean
  signedUrl?: string
  originalFilename?: string
  request: APIRequest<UrlDTO | null>
  lastUpdated: number
}

type UploadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<PurchaseSessionUploadImage>>>
type DownloadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<PurchaseSessionDownloadImage>>>

type ProductStore = {
  [FileManipulationKey.UPLOAD]?: UploadImageStore
  [FileManipulationKey.DOWNLOAD]?: DownloadImageStore
}

/** Purchase session store shape: state[sessionId][productId][productSerial][UPLOAD|DOWNLOAD][filename][type] */
export type PurchaseSessionVisualStoreData = NullableStringIndexSignature<
  Record<number, Record<number, ProductStore> | undefined> | undefined
>

/** THUMB & WEB */
const DOWNLOAD_TYPE_COUNT = 2

interface PurchaseSessionVisualStoreState {
  sessions: PurchaseSessionVisualStoreData

  setUploadProgress: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: Partial<PurchaseSessionUploadImage>) => void
  setUploadComplete: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: Partial<PurchaseSessionUploadImage>) => void
  setUploadError: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, error: string) => void

  handleUploadStart: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: PurchaseSessionUploadImage, originalFilename?: string, replaces?: string) => void
  handleBatchUploadStart: (entries: Array<{ sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: PurchaseSessionUploadImage, originalFilename?: string, replaces?: string }>) => void
  handleDownloadReceived: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: PurchaseSessionDownloadImage) => void

  startDelete: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType) => void
  completeDelete: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType) => void

  getUploadCancelToken: (sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType) => CancelTokenSource | undefined
  getProductStore: (sessionId: string, productId?: number, productSerial?: number) => ProductStore | undefined
  getSessionStore: (sessionId: string) => PurchaseSessionVisualStoreData[string]
}

export const usePurchaseSessionVisualStore = create<PurchaseSessionVisualStoreState>((set, get) => ({
  sessions: {},

  setUploadProgress: (sessionId, productId, productSerial, fileName, type, data) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }
    const uploads = { ...(serial[FileManipulationKey.UPLOAD] ?? {}) }
    const fileTypes = { ...(uploads[fileName] ?? {}) }
    fileTypes[type] = { ...fileTypes[type]!, ...data } as PurchaseSessionUploadImage
    uploads[fileName] = fileTypes
    serial[FileManipulationKey.UPLOAD] = uploads
    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  setUploadComplete: (sessionId, productId, productSerial, fileName, type, data) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }
    const uploads = { ...(serial[FileManipulationKey.UPLOAD] ?? {}) }
    const fileTypes = { ...(uploads[fileName] ?? {}) }
    fileTypes[type] = { ...fileTypes[type]!, ...data, progress: 100 } as PurchaseSessionUploadImage
    uploads[fileName] = fileTypes
    serial[FileManipulationKey.UPLOAD] = uploads
    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  setUploadError: (sessionId, productId, productSerial, fileName, type, error) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }
    const uploads = { ...(serial[FileManipulationKey.UPLOAD] ?? {}) }
    const fileTypes = { ...(uploads[fileName] ?? {}) }
    const existing = fileTypes[type]
    if (existing) {
      fileTypes[type] = { ...existing, progress: 100, request: APIRequest.factoryError('DEFAULT_ERROR' as any, error) as any } as PurchaseSessionUploadImage
    }
    uploads[fileName] = fileTypes
    serial[FileManipulationKey.UPLOAD] = uploads
    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  handleUploadStart: (sessionId, productId, productSerial, fileName, type, data, originalFilename, replaces) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }
    const uploads = { ...(serial[FileManipulationKey.UPLOAD] ?? {}) }
    const fileTypes = { ...(uploads[fileName] ?? {}) }
    fileTypes[type] = data
    uploads[fileName] = fileTypes

    // Delete originalFilename upload entry (when server renames the file)
    if (originalFilename && originalFilename !== fileName && uploads[originalFilename]?.[type]) {
      const origFileTypes = { ...uploads[originalFilename]! }
      delete origFileTypes[type]
      if (Object.keys(origFileTypes).length === 0) {
        delete uploads[originalFilename]
      } else {
        uploads[originalFilename] = origFileTypes
      }
    }

    serial[FileManipulationKey.UPLOAD] = uploads

    // Delete download entry if replacing
    if (replaces && serial[FileManipulationKey.DOWNLOAD]?.[replaces]) {
      const downloads = { ...serial[FileManipulationKey.DOWNLOAD]! }
      delete downloads[replaces]
      serial[FileManipulationKey.DOWNLOAD] = downloads
    }

    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  handleBatchUploadStart: (entries) => set(state => {
    const sessions = { ...state.sessions }
    for (const { sessionId, productId, productSerial, fileName, type, data, originalFilename, replaces } of entries) {
      const session = { ...(sessions[sessionId] ?? {}) }
      const product = { ...(session[productId] ?? {}) }
      const serial: ProductStore = { ...(product[productSerial] ?? {}) }
      const uploads = { ...(serial[FileManipulationKey.UPLOAD] ?? {}) }
      const fileTypes = { ...(uploads[fileName] ?? {}) }
      fileTypes[type] = data
      uploads[fileName] = fileTypes

      if (originalFilename && originalFilename !== fileName && uploads[originalFilename]?.[type]) {
        const origFileTypes = { ...uploads[originalFilename]! }
        delete origFileTypes[type]
        if (Object.keys(origFileTypes).length === 0) {
          delete uploads[originalFilename]
        } else {
          uploads[originalFilename] = origFileTypes
        }
      }

      serial[FileManipulationKey.UPLOAD] = uploads

      if (replaces && serial[FileManipulationKey.DOWNLOAD]?.[replaces]) {
        const downloads = { ...serial[FileManipulationKey.DOWNLOAD]! }
        delete downloads[replaces]
        serial[FileManipulationKey.DOWNLOAD] = downloads
      }

      product[productSerial] = serial
      session[productId] = product
      sessions[sessionId] = session
    }
    return { sessions }
  }),

  handleDownloadReceived: (sessionId, productId, productSerial, fileName, type, data) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }
    const downloads = { ...(serial[FileManipulationKey.DOWNLOAD] ?? {}) }
    const fileTypes = { ...(downloads[fileName] ?? {}) }
    fileTypes[type] = data
    downloads[fileName] = fileTypes
    serial[FileManipulationKey.DOWNLOAD] = downloads

    // If both THUMB & WEB downloaded, remove upload wrapper
    const baseType = downloadVisualTypeToBaseType[type]
    if (serial[FileManipulationKey.UPLOAD]?.[fileName]?.[baseType]) {
      if (Object.keys(fileTypes).length >= DOWNLOAD_TYPE_COUNT) {
        const uploads = { ...serial[FileManipulationKey.UPLOAD]! }
        delete uploads[fileName]
        serial[FileManipulationKey.UPLOAD] = uploads
      }
    }

    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  startDelete: (sessionId, productId, productSerial, fileName, type) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }

    // Mark upload as deleting
    const uploadFile = serial[FileManipulationKey.UPLOAD]?.[fileName]?.[type]
    if (uploadFile) {
      const uploads = { ...serial[FileManipulationKey.UPLOAD]! }
      const uploadFileTypes = { ...uploads[fileName]! }
      uploadFileTypes[type] = { ...uploadFile, deleting: true }
      uploads[fileName] = uploadFileTypes
      serial[FileManipulationKey.UPLOAD] = uploads
    }

    // Mark all download types as deleting
    const downloadWrapper = serial[FileManipulationKey.DOWNLOAD]?.[fileName]
    if (downloadWrapper) {
      const downloads = { ...serial[FileManipulationKey.DOWNLOAD]! }
      const newDownloadWrapper = { ...downloadWrapper }
      for (const t of Object.keys(newDownloadWrapper)) {
        if (newDownloadWrapper[t]) {
          newDownloadWrapper[t] = { ...newDownloadWrapper[t]!, deleting: true }
        }
      }
      downloads[fileName] = newDownloadWrapper
      serial[FileManipulationKey.DOWNLOAD] = downloads
    }

    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  completeDelete: (sessionId, productId, productSerial, fileName, type) => set(state => {
    const sessions = { ...state.sessions }
    const session = { ...(sessions[sessionId] ?? {}) }
    const product = { ...(session[productId] ?? {}) }
    const serial: ProductStore = { ...(product[productSerial] ?? {}) }

    // Remove upload entry
    if (serial[FileManipulationKey.UPLOAD]?.[fileName]?.[type]) {
      const uploads = { ...serial[FileManipulationKey.UPLOAD]! }
      const fileTypes = { ...uploads[fileName]! }
      delete fileTypes[type]
      if (Object.keys(fileTypes).length > 0) {
        uploads[fileName] = fileTypes
      } else {
        delete uploads[fileName]
      }
      serial[FileManipulationKey.UPLOAD] = uploads
    }

    // Remove all download entries
    if (serial[FileManipulationKey.DOWNLOAD]?.[fileName]) {
      const downloads = { ...serial[FileManipulationKey.DOWNLOAD]! }
      delete downloads[fileName]
      serial[FileManipulationKey.DOWNLOAD] = downloads
    }

    product[productSerial] = serial
    session[productId] = product
    sessions[sessionId] = session
    return { sessions }
  }),

  getUploadCancelToken: (sessionId, productId, productSerial, fileName, type) => {
    return get().sessions[sessionId]?.[productId]?.[productSerial]?.[FileManipulationKey.UPLOAD]?.[fileName]?.[type]?.cancelToken
  },

  getProductStore: (sessionId, productId, productSerial) => {
    if (productId === undefined || productSerial === undefined) return undefined
    return get().sessions[sessionId]?.[productId]?.[productSerial]
  },

  getSessionStore: (sessionId) => {
    return get().sessions[sessionId]
  },
}))
